import type { ApiBriefData } from './types'

export const BRIEF_POLL_INTERVAL_MS = 10_000

export function meetingBriefQueryKey(calendarEventId: string) {
  return ['meeting-brief', calendarEventId] as const
}

/** What the calendar GlobalState shell knows about this event's brief. */
export interface BriefPresence {
  /** A generation is in flight or a brief row exists — chase empty/failed caches. */
  pollWhilePending?: boolean
  /** conversationId of the brief the shell knows about, if any. */
  presenceConversationId?: string | null
}

/**
 * Cached 404s and terminal `failed` results never refetch on their own
 * (`staleTime: Infinity`), so poll whenever the calendar shell disagrees with
 * the cache: a brief is pending or present but the cache is empty/failed, a
 * generation or regeneration is in flight, or a different brief conversation
 * landed elsewhere (the cached body is stale).
 */
export function meetingBriefRefetchInterval(
  data: ApiBriefData | null | undefined,
  presence: BriefPresence = {},
): number | false {
  const { pollWhilePending = false, presenceConversationId = null } = presence
  if (!data || data.status === 'failed') {
    return pollWhilePending ? BRIEF_POLL_INTERVAL_MS : false
  }
  if (data.status !== 'ready') {
    return BRIEF_POLL_INTERVAL_MS
  }
  if (data.regenerationConversationId) {
    return BRIEF_POLL_INTERVAL_MS
  }
  if (presenceConversationId && data.conversationId !== presenceConversationId) {
    return BRIEF_POLL_INTERVAL_MS
  }
  return false
}

/** Fresh forever once settled; while the poll is armed, every mount, key change or focus refetches. */
export function meetingBriefStaleTime(data: ApiBriefData | null | undefined, presence: BriefPresence = {}): number {
  return meetingBriefRefetchInterval(data, presence) === false ? Infinity : 0
}

/**
 * Seed the GET cache after a POST reports a generation in flight — this is
 * what starts the poll loop. Never drops a ready body: a regeneration keeps
 * the current brief and records the new conversation.
 */
export function seedGeneratingBrief(current: ApiBriefData | null | undefined, conversationId: string): ApiBriefData {
  if (current?.status === 'ready') {
    return { ...current, regenerationConversationId: conversationId }
  }
  return { status: 'generating', conversationId }
}
